import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Plus } from "lucide-react";
import { Button, SegmentedTabs, Skeleton } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useHasPermission } from "@/hooks/usePermissions";
import {
  useCashSessions,
  useFinancialEntries,
  useFinancialSetup,
  useFinancialTitles,
} from "@/hooks/useFinancial";
import { activeEntries, buildDre, entryDate, todayIso, yearTotal } from "@/lib/financial/calc";
import { downloadCsv } from "@/lib/financial/csv";
import { CashboxTab } from "./CashboxTab";
import { DreTab } from "./DreTab";
import { FinancialFormDialog, type FormMode } from "./FinancialFormDialog";
import { LedgerTab } from "./LedgerTab";
import { periodPresets } from "./helpers";
import { DayPicker, RangePicker, type PeriodState } from "./PeriodPicker";
import { TitlesTab } from "./TitlesTab";
import { useFinancialFormat } from "./useFinancialFormat";

const BASE = "/financial";
type TabKey = "receivables" | "payables" | "cashbox" | "ledger" | "dre";
const TABS: { key: TabKey; seg: string; feature: string }[] = [
  { key: "receivables", seg: "receivables", feature: "financial_receivables" },
  { key: "payables", seg: "payables", feature: "financial_payables" },
  { key: "cashbox", seg: "cashbox", feature: "financial_cashbox" },
  { key: "ledger", seg: "ledger", feature: "financial_ledger" },
  { key: "dre", seg: "dre", feature: "financial_overview" },
];

function useTabPermissions() {
  const rec = useHasPermission("financial_receivables", "view");
  const pay = useHasPermission("financial_payables", "view");
  const cash = useHasPermission("financial_cashbox", "view");
  const ledger = useHasPermission("financial_ledger", "view");
  const overview = useHasPermission("financial_overview", "view");
  return {
    isLoading: rec.isLoading || pay.isLoading || cash.isLoading || ledger.isLoading || overview.isLoading,
    view: { receivables: rec.data, payables: pay.data, cashbox: cash.data, ledger: ledger.data, dre: overview.data } as Record<TabKey, boolean>,
    recCreate: useHasPermission("financial_receivables", "create").data,
    recUpdate: useHasPermission("financial_receivables", "edit").data,
    payCreate: useHasPermission("financial_payables", "create").data,
    payUpdate: useHasPermission("financial_payables", "edit").data,
    cashCreate: useHasPermission("financial_cashbox", "create").data,
    cashUpdate: useHasPermission("financial_cashbox", "edit").data,
    ledgerCreate: useHasPermission("financial_ledger", "create").data,
    ledgerUpdate: useHasPermission("financial_ledger", "edit").data,
  };
}

export default function FinancialPage() {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const location = useLocation();
  const navigate = useNavigate();
  const today = todayIso();
  const perms = useTabPermissions();

  const visibleTabs = TABS.filter((tab) => perms.view[tab.key]);
  const seg = location.pathname.replace(BASE, "").replace(/^\//, "").split("/")[0];
  const active: TabKey = (visibleTabs.find((tab) => tab.seg === seg) ?? visibleTabs[0])?.key ?? "receivables";

  const [period, setPeriod] = useState<PeriodState>(() => {
    const p = periodPresets(today).find((x) => x.key === "thisMonth")!;
    return { from: p.from, to: p.to, presetKey: p.key };
  });
  const [day, setDay] = useState(today);
  const [form, setForm] = useState<FormMode | null>(null);

  const setup = useFinancialSetup();
  const needsEntries = active === "cashbox" || active === "ledger" || active === "dre";
  const receivables = useFinancialTitles("receivable", !!perms.view.receivables);
  const payables = useFinancialTitles("payable", !!perms.view.payables);
  const entries = useFinancialEntries(needsEntries || !!perms.view.ledger);
  const sessions = useCashSessions(!!perms.view.cashbox);

  const accounts = useMemo(() => (setup.data?.accounts ?? []).filter((a) => a.is_active), [setup.data]);
  const categories = setup.data?.categories ?? [];
  const recList = receivables.data ?? [];
  const payList = payables.data ?? [];
  const entryList = entries.data ?? [];
  const sessionList = sessions.data ?? [];
  const todaySession = sessionList.find((s) => s.business_date === today);

  const badges: Partial<Record<TabKey, string>> = {
    receivables: String(recList.filter((x) => x.status === "open").length),
    payables: String(payList.filter((x) => x.status === "open").length),
    cashbox: todaySession ? (todaySession.closed_at ? t("header.cashClosed") : t("header.cashOpen")) : undefined,
  };

  const canCreateEntry = !!perms.ledgerCreate;

  const exportCsv = () => {
    const catById = new Map(categories.map((c) => [c.id, c]));
    const accById = new Map(accounts.map((a) => [a.id, a]));
    const stamp = today;
    if (active === "receivables" || active === "payables") {
      const list = active === "receivables" ? recList : payList;
      downloadCsv(`${t("export.fileTitles")}-${stamp}.csv`, [
        [t("titlesTable.due"), t(active === "receivables" ? "titlesTable.client" : "titlesTable.payee"), t("form.detail"), t("titlesTable.about"), t("form.category"), t("titlesTable.method"), t("titlesTable.amount"), ""],
        ...list
          .filter((x) => x.due_date >= period.from && x.due_date <= period.to)
          .map((x) => [
            fmt.dnum(x.due_date), x.counterparty_name, x.counterparty_detail ?? "", x.description,
            fmt.category(catById.get(x.category_id ?? "")), fmt.method(x.payment_method), fmt.moneyInput(Number(x.amount)),
            x.status === "paid" ? t(active === "receivables" ? "titlesTable.statusReceived" : "titlesTable.statusPaid") : "",
          ]),
      ]);
    } else if (active === "dre") {
      const year = Number(today.slice(0, 4));
      const dre = buildDre(entryList, categories, year, today);
      const months = Array.from({ length: 12 }, (_, m) => `${fmt.monthShort(m)}/${String(year).slice(2)}`);
      const money = (x: number) => `${x < 0 ? "-" : ""}${fmt.moneyInput(x)}`;
      const line = (label: string, v: (number | null)[]) => [label, ...v.map((x) => (x === null ? "" : money(x))), money(yearTotal(v))];
      downloadCsv(`${t("export.fileDre")}-${year}.csv`, [
        [t("dre.description"), ...months, t("dre.year")],
        ...(["rob", "ded", "cat", "pes", "adm", "fin", "luc"] as const).flatMap((g) => [
          line(t(`dre.groups.${g}`), dre.groups[g]),
          ...categories.filter((c) => c.dre_group === g).map((c) => line(`  ${fmt.category(c)}`, dre.categories.get(c.id) ?? [])),
        ]),
        line(t("dre.rol"), dre.rol),
        line(t("dre.ebitda"), dre.ebitda),
        line(t("dre.net"), dre.net),
        line(t("dre.after"), dre.afterWithdrawals),
      ]);
    } else {
      const inRange = activeEntries(entryList).filter((e) => {
        const d = entryDate(e);
        return active === "cashbox" ? d === day : d >= period.from && d <= period.to;
      });
      downloadCsv(`${t(active === "cashbox" ? "export.fileCashbox" : "export.fileLedger")}-${active === "cashbox" ? day : stamp}.csv`, [
        [t("ledger.date"), t("ledger.what"), t("ledger.category"), t("ledger.account"), t("titlesTable.method"), t("ledger.origin"), t("ledger.amount")],
        ...inRange.map((e) => [
          fmt.dnum(entryDate(e)), e.description, fmt.category(catById.get(e.category_id ?? "")), fmt.account(accById.get(e.account_id)),
          fmt.method(e.payment_method), t(`ledger.origins.${e.origin}`), `${Number(e.amount) < 0 ? "-" : ""}${fmt.moneyInput(Number(e.amount))}`,
        ]),
      ]);
    }
    toast({ title: t("toasts.exported") });
  };

  const subtitle =
    active === "cashbox"
      ? day === today ? t("subtitles.cashboxToday") : t("subtitles.cashboxOther", { date: fmt.dnum(day) })
      : t(`subtitles.${active}`);

  const loading = perms.isLoading || setup.isLoading;
  const dataLoading =
    (active === "receivables" && receivables.isLoading) ||
    (active === "payables" && payables.isLoading) ||
    (active === "cashbox" && (sessions.isLoading || entries.isLoading)) ||
    ((active === "ledger" || active === "dre") && entries.isLoading);
  const error = setup.isError || receivables.isError || payables.isError || entries.isError || sessions.isError;

  const showPeriod = active === "receivables" || active === "payables" || active === "ledger";

  return (
    <div className="mx-auto w-full max-w-[1440px] py-6">
      <header className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <h1 className="font-display text-2xl font-bold tracking-tight">{t(`titles.${active}`)}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* cada aba tem uma única ação de criar; caixa e lançamentos têm o botão dentro da própria aba */}
          {perms.recCreate && active === "receivables" ? (
            <Button onClick={() => setForm({ type: "charge" })}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              {t("header.newCharge")}
            </Button>
          ) : null}
          {perms.payCreate && active === "payables" ? (
            <Button onClick={() => setForm({ type: "expense" })}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              {t("header.newExpense")}
            </Button>
          ) : null}
          <Button variant="outline" onClick={exportCsv} disabled={loading}>
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            {t("header.export")}
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="flex flex-col gap-3" aria-busy="true" aria-label={t("loading")}>
          <Skeleton label={t("loading")} className="h-10 w-full max-w-xl" />
          <Skeleton label={t("loading")} className="h-28 w-full" />
          <Skeleton label={t("loading")} className="h-72 w-full" />
        </div>
      ) : !visibleTabs.length ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t("noAccess")}</p>
      ) : (
        <>
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <SegmentedTabs
              variant="outline"
              scrollable="mobile"
              label={t("header.tabsLabel")}
              value={active}
              onValueChange={(key) => navigate(key === visibleTabs[0].key ? BASE : `${BASE}/${key}`)}
              items={visibleTabs.map((tab) => ({
                value: tab.key,
                label: badges[tab.key] ? `${t(`tabs.${tab.key}`)} · ${badges[tab.key]}` : t(`tabs.${tab.key}`),
              }))}
            />
            {showPeriod ? <RangePicker value={period} onChange={setPeriod} today={today} /> : null}
            {active === "cashbox" ? <DayPicker value={day} onChange={setDay} today={today} /> : null}
          </div>

          {error ? (
            <p role="alert" className="py-10 text-center text-sm text-destructive">{t("loadError")}</p>
          ) : dataLoading ? (
            <div className="flex flex-col gap-3" aria-busy="true" aria-label={t("loading")}>
              <Skeleton label={t("loading")} className="h-28 w-full" />
              <Skeleton label={t("loading")} className="h-72 w-full" />
            </div>
          ) : (
            <>
              {active === "receivables" || active === "payables" ? (
                <TitlesTab
                  key={active}
                  kind={active === "receivables" ? "receivable" : "payable"}
                  titles={active === "receivables" ? recList : payList}
                  accounts={accounts}
                  period={period}
                  today={today}
                  canUpdate={!!(active === "receivables" ? perms.recUpdate : perms.payUpdate)}
                  onForm={setForm}
                />
              ) : null}
              {active === "cashbox" ? (
                <CashboxTab
                  day={day}
                  today={today}
                  onDay={setDay}
                  sessions={sessionList}
                  entries={entryList}
                  accounts={accounts}
                  categories={categories}
                  canCreate={!!perms.cashCreate}
                  canUpdate={!!perms.cashUpdate}
                  onForm={setForm}
                />
              ) : null}
              {active === "ledger" ? (
                <LedgerTab
                  entries={entryList}
                  accounts={accounts}
                  categories={categories}
                  period={period}
                  onPeriod={setPeriod}
                  today={today}
                  canCreate={canCreateEntry}
                  canUpdate={!!perms.ledgerUpdate}
                  onForm={setForm}
                />
              ) : null}
              {active === "dre" ? (
                <DreTab entries={entryList} categories={categories} accounts={accounts} today={today} canEdit={!!perms.ledgerUpdate} />
              ) : null}
            </>
          )}
        </>
      )}

      <FinancialFormDialog
        mode={form}
        onClose={() => setForm(null)}
        accounts={accounts}
        categories={categories}
        today={today}
        onCreatedTitle={(kind) => {
          const target = kind === "receivable" ? "receivables" : "payables";
          if (perms.view[target]) navigate(target === visibleTabs[0]?.key ? BASE : `${BASE}/${target}`);
        }}
      />
    </div>
  );
}
